import {
  Box,
  Button,
  Card,
  CardActionArea,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { EmphasizedNumber, Icons, InformationModal } from 'utoniq-ui'

import {
  EscottMasterInput,
  Maybe,
  PaymentMethod,
  PrepaidPointsPaymentSourceFragment,
  PurchasablePrepaidPoint,
  PurchasablePrepaidPointConnectionFragment,
  PurchasablePrepaidPointMetadata,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import { LegalInformationLinksCell } from '@/components/organisms/legalInformation'
import { PaymentMethodSelectDrawer } from '@/components/organisms/point/PaymentMethodSelectDrawer'
import { constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Organism, Template } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'
import { isCarrierPaymentMethod } from '@/lib/escott'
export * from './fragments'
export type PrepaidPointPlan = Pick<
  PurchasablePrepaidPoint,
  'id' | 'points' | 'price' | 'description' | 'prepaidPointId'
> & {
  metadata: Pick<PurchasablePrepaidPointMetadata, 'id' | 'platform'>
}
export type State = {
  loading: boolean
  hasNextPage: boolean
  point: Maybe<number>
  purchasablePrepaidPoints: Maybe<PurchasablePrepaidPointConnectionFragment>
  chargeLoading: boolean
  paymentSource: Maybe<PrepaidPointsPaymentSourceFragment>
  /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
  enablePointCarrierPayment: boolean
}
export type Action = {
  onClickCharge: (
    plan: PrepaidPointPlan,
    paymentMethod: Maybe<PaymentMethod>,
    escottInput: EscottMasterInput
  ) => Promise<void>
  onFetchMore: () => Promise<void>
}

export const PrepaidPointChargeTemplate: Template<State, Action> = ({
  state,
  onClickCharge,
  onFetchMore,
}) => {
  const { t } = useTranslation('prepaidPointCharge')
  const { router, routerNames, routerPages, generatePath } = useRouter<{
    status?: string
    point?: number
  }>()
  const isWeb = getPlatform() === 'web'
  const { showMessage, showError } = useMessage()
  const { analytics } = useAnalytics()
  const [purchasedPlan, setPurchasedPlan] = useState<PrepaidPointPlan | null>(
    null
  )
  /** 決済手段 */
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [openPaymentMethodSelectDrawer, setOpenPaymentMethodSelectDrawer] =
    useState(false)
  const [openCompleteModal, setOpenCompleteModal] = useState(false)
  const [openCompleteCarrierPaymentModal, setOpenCompleteCarrierPaymentModal] =
    useState(false)
  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  const handlePointClick = async (plan: PrepaidPointPlan) => {
    setPurchasedPlan(plan)

    if (isWeb) {
      setOpenPaymentMethodSelectDrawer(true)
    } else {
      // ネイティブの場合ポイントを含むデジタルアイテムの決済は
      // IAP を経由して実行するため
      // プラン選択後すぐに確認モーダルを表示する
      setOpenConfirmModal(true)
    }
  }

  const handlePointCharge = async (paymentMethod: Maybe<PaymentMethod>) => {
    if (!purchasedPlan || (isWeb && !paymentMethod)) {
      setOpenConfirmModal(false)
      setOpenPaymentMethodSelectDrawer(false)
      return
    }
    onClickCharge(purchasedPlan, paymentMethod, {
      // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
      successUrl: `${window.location.origin}${generatePath(
        routerPages.PrepaidPointCharge
      )}?point=${purchasedPlan.points}`,
      // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
      cancelUrl: `${window.location.origin}${generatePath(
        routerPages.PrepaidPointCharge
      )}`,
      // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
      errorUrl: `${window.location.origin}${generatePath(
        routerPages.PrepaidPointCharge
      )}`,
    })
      .then(() => {
        // キャリア決済の場合は外部キャリア決済画面にて
        // 追加手続きが必要なため完了モーダルを表示しない
        if (isCarrierPaymentMethod(paymentMethod)) return

        setOpenConfirmModal(false)
        setOpenPaymentMethodSelectDrawer(false)
        setOpenCompleteModal(true)
      })
      .catch(e => {
        switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
          /**
           * @description
           * 3Dセキュア認証が必要な場合
           */
          case constants.errorCodes.REQUIRE_3D_SECURE:
            setOpenConfirmModal(false)
            setOpenPaymentMethodSelectDrawer(false)
            setOpenRequire3DSecureModal(true)
            break
          /**
           * @desciption
           * カードが有効期限切れの場合
           */
          case constants.errorCodes.EXPIRED_CARD:
            setOpenConfirmModal(false)
            setOpenPaymentMethodSelectDrawer(false)
            setOpenExpiredCardModal(true)
            break
          /**
           * @description
           * 何かしらの決済エラーの場合
           */
          case constants.errorCodes.PAYMENT_ERROR:
            setOpenConfirmModal(false)
            setOpenPaymentMethodSelectDrawer(false)
            setOpenPaymentErrorModal(true)
            break
          default:
            setOpenConfirmModal(false)
            setOpenPaymentMethodSelectDrawer(false)
            showError(e)
            break
        }
      })
  }

  const handleClosePaymentMethodSelectDrawer = () => {
    if (purchasedPlan) {
      analytics?.track('click_cancel', {
        productId: purchasedPlan.metadata.id,
        prepaidPointId: purchasedPlan.prepaidPointId,
        type: 'bottomsheet',
        name: 'purchasePoint',
      })
    }
    setOpenPaymentMethodSelectDrawer(false)
  }

  // 外部キャリア決済画面からのリダイレクトで戻ってきた際
  // トーストメッセージを表示する処理
  useEffect(() => {
    // キャリア決済画面からのリダイレクトで戻ってきたとき以外は何もしない
    // リダイレクト時にはクエリパラメータに status が含まれる
    if (!router.query.status) return

    // ポイント残高が取得できていない場合は何もしない
    if (state.loading || state.point === null) return

    switch (router.query.status) {
      case 'success':
        setOpenCompleteCarrierPaymentModal(true)
        break
      case 'cancel':
        showMessage(t('carrierCancel'))
        break
      case 'error':
        showError(t('carrierError'))
        break
      default:
        break
    }
  }, [state.loading, router.query.status])

  return (
    <BaseLayout
      margin
      header={{
        title: t('title'),
        back: true,
        defaultBackPage: routerPages.Wallet,
      }}
    >
      <Head title={routerNames.PrepaidPointCharge} />
      {state.loading && state.point === null ? (
        <>
          <PointDisplaySkeletonCell />
        </>
      ) : (
        <PointDisplayCell point={state.point} />
      )}

      <Stack spacing={4} sx={{ pt: 4 }}>
        <ScrollFetcher
          componentProps={{ sx: { pt: 2, pb: 2 }, spacing: 4 }}
          nodes={state.purchasablePrepaidPoints?.edges?.map(
            ({ node }, index) => (
              <PointChargeCardCell
                key={index}
                plan={node}
                onClick={() => {
                  analytics?.track('click_point', {
                    productId: node.metadata.id,
                    prepaidPointId: node.prepaidPointId,
                    name: 'prepaidPointCharge',
                  })
                  return handlePointClick(node)
                }}
              />
            )
          )}
          onFetchMore={onFetchMore}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          emptyMessage={t('emptyPurchasablePrepaidPoints')}
          loadingComponent={
            <Stack spacing={4} sx={{ pb: 4 }}>
              {Array.from(Array(6).keys()).map(key => (
                <PointChargeCardSkeletonCell key={key} />
              ))}
            </Stack>
          }
        />
      </Stack>

      <LegalInformationLinksCell />

      <PaymentMethodSelectDrawer
        open={openPaymentMethodSelectDrawer}
        onClose={handleClosePaymentMethodSelectDrawer}
        paymentSource={state.paymentSource}
        chargeLoading={state.chargeLoading}
        enablePointCarrierPayment={state.enablePointCarrierPayment}
        onSubmit={paymentMethod => {
          setPaymentMethod(paymentMethod)
          setOpenConfirmModal(true)
        }}
      />

      <ConfirmPointChargeModal
        loading={state.chargeLoading}
        onConfirm={() => handlePointCharge(paymentMethod)}
        open={openConfirmModal}
        onCancel={() => {
          if (purchasedPlan) {
            analytics?.track('click_cancel', {
              productId: purchasedPlan.metadata.id,
              prepaidPointId: purchasedPlan.prepaidPointId,
              type: 'modal',
              name: 'purchasePoint',
            })
          }
        }}
        onClose={() => setOpenConfirmModal(false)}
        point={purchasedPlan?.points}
        price={purchasedPlan?.price}
      />

      <CompletePointChargeModal
        open={openCompleteModal}
        onClose={() => setOpenCompleteModal(false)}
        point={purchasedPlan?.points}
      />

      <CompleteCarrierPaymentModal
        open={openCompleteCarrierPaymentModal}
        onClose={() => setOpenCompleteCarrierPaymentModal(false)}
        point={router.query.point}
      />

      <ExpiredCardModal
        open={openExpiredCardModal}
        onClose={() => setOpenExpiredCardModal(false)}
      />

      <PaymentErrorModal
        open={openPaymentErrorModal}
        onClose={() => setOpenPaymentErrorModal(false)}
      />

      <Require3DSecureModal
        open={openRequire3DSecureModal}
        onClose={() => setOpenRequire3DSecureModal(false)}
      />
    </BaseLayout>
  )
}

type PointDisplayCell = {
  point: State['point']
}
export const PointDisplayCell: Cell<PointDisplayCell> = ({ point }) => {
  const { t } = useTranslation('prepaidPointCharge')

  return (
    <CellFrame sx={{ py: 2, textAlign: 'center' }}>
      <Typography variant="h6" color="text.secondary">
        {t('pointBalance')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Icons.Point fontSize="large" />
        <EmphasizedNumber variant="h1" sx={{ ml: 2 }}>
          {point}
        </EmphasizedNumber>
      </Box>
    </CellFrame>
  )
}

const PointDisplaySkeletonCell: Cell = () => {
  return (
    <Stack spacing={2} alignItems="center" flex={1} sx={{ pt: 2, pb: 3 }}>
      <Skeleton variant="rectangular" width={96} height={16} />
      <Skeleton variant="rectangular" width={96} height={36} />
    </Stack>
  )
}

type PointChargeCardCell = {
  plan: PrepaidPointPlan
  onClick: () => void
}
export const PointChargeCardCell: Cell<PointChargeCardCell> = ({
  plan,
  onClick,
}) => {
  const { points, price, description } = plan || {}

  return (
    <CellFrame>
      <Card>
        <CardActionArea onClick={onClick}>
          <Stack direction="row" alignItems="center" sx={{ px: 5, py: 4 }}>
            <Icons.Point fontSize="medium" />
            <EmphasizedNumber variant="h5" sx={{ ml: 2 }}>
              {points}
            </EmphasizedNumber>
            <EmphasizedNumber
              variant="overline"
              color="primary.main"
              sx={{ ml: 2 }}
            >
              {description}
            </EmphasizedNumber>
            <EmphasizedNumber variant="h5" sx={{ ml: 'auto' }}>
              ¥{price}
            </EmphasizedNumber>
          </Stack>
        </CardActionArea>
      </Card>
    </CellFrame>
  )
}

type ConfirmPointChargeModalProps = {
  loading: boolean
  open: boolean
  onCancel: () => void
  onClose: () => void
  onConfirm: () => Promise<void>
  point?: number
  price?: number
}
const ConfirmPointChargeModal: Organism<ConfirmPointChargeModalProps> = ({
  loading,
  open,
  onClose,
  onCancel,
  onConfirm,
  point,
  price,
}) => {
  const { t } = useTranslation('prepaidPointCharge')
  return (
    <InformationModal
      open={open}
      onClose={() => {
        onCancel()
        onClose()
      }}
      title={t('confirmCharge')}
      content={t('confirmChargeDescription', { point, price })}
      buttons={
        <>
          <Button
            sx={{ minWidth: 212 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={onConfirm}
            loading={loading}
          >
            {t('toBuy')}
          </Button>
          <Button
            sx={{ minWidth: 212 }}
            variant="text"
            color="primary"
            onClick={onClose}
            disabled={loading}
          >
            {t('cancel')}
          </Button>
        </>
      }
    />
  )
}

type CompletePointChargeModalProps = {
  open: boolean
  onClose: () => void
  point?: number
}
export const CompletePointChargeModal: Organism<CompletePointChargeModalProps> =
  ({ open, point, onClose }) => {
    const { t } = useTranslation('prepaidPointCharge')
    return (
      <InformationModal
        open={open}
        onClose={onClose}
        title={t('completeCharge')}
        content={t('completeChargeDescription', { point })}
        buttons={
          <Button
            sx={{ minWidth: 212 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={onClose}
          >
            OK
          </Button>
        }
      />
    )
  }

type CompleteCarrierPaymentModalProps = {
  open: boolean
  onClose: () => void
  point?: number
}
export const CompleteCarrierPaymentModal: Organism<CompleteCarrierPaymentModalProps> =
  ({ open, point, onClose }) => {
    const { t } = useTranslation('prepaidPointCharge')
    return (
      <InformationModal
        open={open}
        onClose={onClose}
        title={t('completeCharge')}
        content={t('completeChargeDescription', { point })}
        buttons={
          <Button
            sx={{ minWidth: 212 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={onClose}
          >
            OK
          </Button>
        }
      />
    )
  }

const PointChargeCardSkeletonCell: Cell = () => (
  <Skeleton
    variant="rectangular"
    width="100%"
    height={56}
    sx={{ borderRadius: '2px' }}
  />
)
